import '.a{display:flex;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.a8{gap:4px;}.ssr-plugin'
import '.d8{border-radius:999px;}.ssr-plugin'
import '.e2{padding:8px 12px ;}.ssr-plugin'
import '.e3{transition:width 0.3s ease-in-out, background 0.3s ease-in-out;}.ssr-plugin'
import '.s{position:relative;}.ssr-plugin'
import React, {
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react'
import { Button } from './Button'
import { Text } from './Text'
import { styled } from 'inlines'
import { Icon } from './Icon'
import { useTheme } from '../providers/useTheme'
import { useAppCtx } from '../providers/useAppCtx'
import { useTranslations } from '../hooks/useTranslations'
import { capitalize } from '@based/ui'
import { useContentQuery } from '../hooks/useContentQuery'

export type VoteButtonProps = {
  votes?: number
  maxVotes?: number
  isInverted?: boolean
  on?: {
    change?: (votes: number) => void
  }
}

export const VoteButton: React.FC<VoteButtonProps> = memo(
  ({ votes: initialVotes = 0, maxVotes, on }) => {
    const { votesCount } = useAppCtx()
    const { data: dictionary } = useTranslations('voting')
    const { colors } = useTheme()
    const { data: sequence = {} } = useContentQuery('sequence')
    const [votes, setVotes] = useState<number>(initialVotes)
    const prevSequenceIdRef = useRef(sequence?.id)

    useEffect(() => {
      if (typeof initialVotes === 'number') {
        setVotes(initialVotes)
      }
    }, [initialVotes])

    useEffect(() => {
      if (sequence?.id && prevSequenceIdRef.current !== sequence.id) {
        setVotes(0)
        on?.change?.(0)
        prevSequenceIdRef.current = sequence.id
      }
    }, [sequence?.id, on])

    const colorWithAlpha = (color: string, alpha: number): string => {
      if (color.startsWith('rgba')) {
        return color.replace(/[\d.]+\)$/, `${alpha})`)
      }
      if (color.startsWith('rgb')) {
        return color.replace('rgb', 'rgba').replace(')', `, ${alpha})`)
      }
      const r = parseInt(color.slice(1, 3), 16)
      const g = parseInt(color.slice(3, 5), 16)
      const b = parseInt(color.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    const primary15PercentOpacity = colorWithAlpha(colors.primaryAccent, 0.15)

    const contentColor = votes < 1 ? colors.primaryAccent : colors.background
    const containerBackground = !votes
      ? primary15PercentOpacity
      : colors.primaryAccent

    const updateVotes = useCallback(
      (next: number) => {
        setVotes(next)
        on?.change?.(next)
      },
      [on],
    )

    const isIncrementDisabled = useMemo(() => {
      return maxVotes !== undefined && votesCount >= maxVotes
    }, [maxVotes, votesCount])

    const handlePrimaryClick = useCallback(() => {
      const nextVotes = votes === 0 ? 1 : Math.max(0, votes - 1)
      if (votes === 0 && isIncrementDisabled) return
      if (maxVotes !== undefined && nextVotes <= maxVotes) {
        updateVotes(nextVotes)
      }
    }, [votes, updateVotes, isIncrementDisabled, maxVotes])

    const handleIncrementClick = useCallback(() => {
      if (isIncrementDisabled) return
      const nextVotes = votes + 1
      if (maxVotes !== undefined && nextVotes <= maxVotes) {
        updateVotes(nextVotes)
      }
    }, [votes, updateVotes, isIncrementDisabled, maxVotes])

    const primaryAriaLabel = capitalize(
      (votes === 0
        ? dictionary?.add_vote_button
        : dictionary?.remove_vote_button) ?? '',
    )
    const incrementAriaLabel = capitalize(dictionary?.add_vote_button ?? '')

    return (
      <div className="a d a8 d8 e2 e3 s"
        style={{
          paddingLeft: votes ? 8 : 12,
          paddingRight: votes ? 12 : 16,
          width: votes ? 96 : 'auto',
          background: containerBackground,
          opacity: maxVotes === votesCount && !votes ? 0.5 : 1,
          cursor: maxVotes === votesCount && !votes ? 'not-allowed' : 'pointer',
        }}
        role="group"
        onClick={() => (!votes ? handlePrimaryClick() : () => {})}
      >
        <Button
          id="vote-button-primary-action"
          isRaw
          aria-label={primaryAriaLabel}
          on={{ click: handlePrimaryClick }}
        >
          <Icon
            variant={'remove'}
            color={contentColor}
            aria-hidden={true}
            style={{
              display: votes > 0 ? 'block' : 'none',
            }}
          />
        </Button>

        <Text
          isNotSelectable
          variant="subheading-bold"
          color={contentColor}
          style={{
            minWidth: 22,
            minHeight: 24,
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          {votes === 0 ? dictionary?.vote_button_text : String(votes)}
        </Text>

        <Button
          id="vote-button-add-vote"
          isRaw
          aria-label={incrementAriaLabel}
          on={{ click: handleIncrementClick }}
          isDisabled={isIncrementDisabled}
          style={{
            position: 'absolute',
            left: votes > 0 ? 64 : 8,
            opacity: votes ? 1 : 0,
            top: 8,
            display: 'block',
            transition: 'left 0.15s ease-in-out, top 0.15s ease-in-out',
          }}
        >
          <Icon variant="add" color={contentColor} aria-hidden={true} />
        </Button>
      </div>
    )
  },
)
